import './PresimReportWindow.scss'
import { Fragment, useEffect, useMemo, useState } from 'react'
import {
    HeaderAction,
    HeaderActionLabel,
    HeaderActionsContainer,
    Modal,
    ModalBody,
    ModalHeader,
    ModalModeType
} from '../../../../../CommonComponents/modals/Modal'
import { CloseSvg, DownloadSvg, WindowRightSvg, WindowResizeSvg, WindowDetachSvg } from '../../../../../CommonComponents/svgs/Svgs'
import { useSmartPlacement } from '../../../../../CommonComponents/modals/hooks/useSmartPlacement'
import useReportWindow from '../../../hooks/useReportWindow'
import useAssets from '../../../hooks/useAssets'
import useSelectedGroupSatellite from '../../../hooks/useSelectedGroupSatellite'
import { PresimDataType, WindowKeys } from '../../../utils/scenario-types'
import GraphFrame from './GraphFrame'
import useScenarioResponse from '../../../hooks/useScenarioResponse'
import useUserData from '../../../hooks/useUserData'
import usePresimApis from '../../../hooks/usePresimApis'
import useNavigation from '../../../hooks/useNavigation'
import { showToastMessage } from '../../../../../../utils/commonFunctions'
import { CircularProgress } from '@mui/material'
import { SCENARIO_OPERATION_TYPES } from '../../../../constants'
import { REPORT_WINDOWS } from '../../../utils/constants'


const PresimReportWindow = () => {
    const { presim } = useAssets()
    const { userData } = useUserData()
    const { missionId, scenarioId } = useNavigation()
    const { handleExportTelemetry } = usePresimApis()
    const { scenarioResponse } = useScenarioResponse()
    const { selectedGroupSatellite } = useSelectedGroupSatellite()
    const { windowStore, onReportModalChange } = useReportWindow()

    const [exportLoading, setExportLoading] = useState(false)

    const presimTabs = useMemo(() => REPORT_WINDOWS.presimReport.tabs, [REPORT_WINDOWS.presimReport.tabs])
    const presimModalDetails = useMemo(() => windowStore.modals.presimReport, [windowStore.modals.presimReport])

    const windowSize = useMemo(() => windowStore.modals.presimReport.windowSize, [windowStore.modals.presimReport.windowSize])
    const selectedTab = useMemo(() => presimModalDetails.selected as WindowKeys, [presimModalDetails.selected])

    const presimData = useMemo(() => presim?.data || {}, [presim?.data])
    const scenarioName = useMemo(() => scenarioResponse?.scenarioName, [scenarioResponse?.scenarioName])
    const selectedGroup = useMemo(() => selectedGroupSatellite?.groupId || null, [selectedGroupSatellite?.groupId])
    const selectedSatellite = useMemo(() => selectedGroupSatellite?.satelliteId || null, [selectedGroupSatellite?.satelliteId])

    const groups = useMemo(() => scenarioResponse?.satelliteGroups || [], [scenarioResponse?.satelliteGroups])
    const satellites = useMemo(() => groups.find(gp => gp.satelliteGroupId === selectedGroup)?.satellites || [], [groups, selectedGroup])
    const selectedSatelliteName = useMemo(() => satellites.find(sat => sat.satelliteId === selectedSatellite)?.satelliteName || '', [satellites, selectedSatellite])

    const scenarioOperation = useMemo(() => scenarioResponse?.scenarioOperation, [scenarioResponse?.scenarioOperation])
    const selectedPresimData = useMemo(() => selectedSatellite && presimData[selectedSatellite] || {} as PresimDataType[string], [presimData, selectedSatellite])
    const isLeops = useMemo(() => scenarioOperation === SCENARIO_OPERATION_TYPES.LEOP, [scenarioOperation])

    const notDetachedWindows = useMemo(() => {
        const ignoredTabsforLeops = isLeops ? ['dataflow', 'thermal'] : userData?.['feature-allowed']?.['thermal'] === 'No' ? ['thermal'] : []
        return Object.entries(presimModalDetails.windows)
            .map(([name, window]) => ({ name, ...window }))
            .filter(w => !w.detached && !ignoredTabsforLeops.includes(w.name))
    }, [presimModalDetails.windows, isLeops, userData?.['feature-allowed']?.['thermal']])

    const { open, pinned, zIndex, position } = useMemo(() => windowStore?.modals?.presimReport, [windowStore?.modals?.presimReport])

    const { top, left, bottom, right } = useSmartPlacement(
        "presim__report__button",
        "presim__report__modal",
        "top",
        open,
        pinned,
        {
            top: position.top,
            right: position.right,
            bottom: position.bottom,
            left: position.left
        }
    );



    useEffect(() => {
        window.addEventListener("blur", function (e) {
            setTimeout(function () {
                window.focus();
            }, 0);
        });
    }, [])

    const getDataGraph = useMemo(() => {
        switch (selectedTab) {
            case 'power':
                return {
                    title: 'POWER',
                    modalId: 'power',
                    graphs: selectedPresimData?.result?.powerUrl ?? []
                }
            case 'adcs':
                return {
                    title: 'ADCS',
                    modalId: 'adcs',
                    graphs: selectedPresimData?.result?.agilityUrl ?? []
                }
            case 'dataflow':
                return {
                    title: 'DATA FLOW',
                    modalId: 'dataflow',
                    graphs: selectedPresimData?.result?.dataFlowUrl ?? []
                }
            case 'thermal':
                return {
                    title: 'THERMAL',
                    modalId: 'thermal',
                    graphs: selectedPresimData?.result?.thermalUrl ?? []
                }
            default:
                return {
                    title: 'POWER',
                    modalId: 'power',
                    graphs: selectedPresimData?.result?.powerUrl ?? []
                }
        }
    }, [selectedTab, selectedPresimData])

    const exportTelemetry = async () => {
        if (!selectedGroup || !selectedSatellite || !scenarioName || !selectedSatelliteName || !missionId || !scenarioId) return
        setExportLoading(true)
        try {
            const response = await handleExportTelemetry(missionId, scenarioId, selectedGroup, selectedSatellite, selectedSatelliteName, scenarioName)
            const message = response?.message
            showToastMessage('success', message)
        } catch (error: any) {
            showToastMessage('error', error.message)
        } finally {
            setExportLoading(false)
        }
    }

    const onClick = (e: React.MouseEvent, mode: ModalModeType, windowKey?: WindowKeys) => {
        onReportModalChange(e, 'root', 'presimReport', mode, windowKey)
    }

    const onSelect = (e: React.MouseEvent, tab: WindowKeys) => {
        onReportModalChange(e, 'child', 'presimReport', 'select_tab', tab)
    }


    return (
        <Modal id='presim__report__modal' open={open} pinned={pinned} className={`presim__report__container`}
            style={{
                width: `${position.width}%`,
                height: `${position.height}%`,
                top: top,
                right: right,
                bottom: bottom,
                left: left,
                zIndex: zIndex
            }}
            onClick={(e) => { onClick(e, 'focus', selectedTab) }}
        >
            <ModalHeader className='presim__report__container__header'>
                <HeaderActionsContainer className='presim__buttons__container'>
                    {presimTabs.map((tab, index) => {
                        const ignoredTabsforLeops = ['dataflow', 'thermal']
                        const lastTab = notDetachedWindows.length === 1
                        if (isLeops && ignoredTabsforLeops.includes(tab.value)) return null
                        else if (userData?.['feature-allowed']?.['thermal'] === 'No' && tab.value === 'thermal') return null
                        else if (presimModalDetails.windows[tab.value].open && presimModalDetails.windows[tab.value].detached) return null
                        return (
                            <Fragment key={index}>
                                <HeaderAction title={tab.label} className={`presim__action__button 
                                ${tab.value === selectedTab && 'active'}
                                ${lastTab && 'last__tab'}
                                `}
                                    onClick={(e) => { onSelect(e, tab.value as WindowKeys) }}>
                                    {tab.icon}
                                </HeaderAction>
                                {lastTab && <HeaderActionLabel label={tab.label} />}
                            </Fragment>
                        )
                    })}
                </HeaderActionsContainer>
                <HeaderActionsContainer>
                    <div className='d-flex align-items-center gap-3'>
                        <div className='d-flex align-items-center gap-2'>
                            <HeaderAction title={pinned ? 'Unpin' : 'Pin to Right'}
                                className={`${pinned && 'active'}`}
                                onClick={(e) => { onClick(e, 'pinned') }}
                            >
                                <WindowRightSvg />
                            </HeaderAction>
                            <HeaderAction title='Resize'
                                onClick={(e) => { onClick(e, 'resize') }}
                            >
                                <WindowResizeSvg size={windowSize} />
                            </HeaderAction>
                            {presimTabs.map((tab) => {
                                if (selectedTab !== tab.value) return null
                                else if (notDetachedWindows.length <= 1) return null
                                return (
                                    <HeaderAction title='Detach'
                                        onClick={(e) => { onReportModalChange(e, 'child', 'presimReport', 'detached', selectedTab) }}
                                    >
                                        <WindowDetachSvg />
                                    </HeaderAction>
                                )
                            })}
                        </div>
                        <div className='d-flex align-items-center gap-2'>
                            <HeaderAction title='Export'
                                onClick={exportTelemetry}
                            >
                                {exportLoading ? <CircularProgress size={14} color="inherit" /> : <DownloadSvg />}
                            </HeaderAction>
                            <HeaderAction title='Close'
                                onClick={(e) => { onClick(e, 'close') }}
                            >
                                <CloseSvg />
                            </HeaderAction>
                        </div>
                    </div>
                </HeaderActionsContainer>
            </ModalHeader>
            <ModalBody className='overflow-hidden'>
                <GraphFrame {...getDataGraph} />
            </ModalBody>
        </Modal >
    )
}

export default PresimReportWindow